import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { AttendanceDashboardPeriodFilter } from 'components/molecules/AttendanceDashboardPeriodFilter';
import { AttendanceTrackingPageHeader } from 'components/molecules/AttendanceTrackingPageHeader';
import { ManagerAttendancePanel } from '../ManagerAttendancePanel';
import { agent } from 'services/service';
import {
  AttendanceEmployeeFilter,
  AttendanceEmployeeResponse,
  AttendanceEmployeeRow,
  AttendanceEmployeeSummary
} from 'services/AttendanceTrackingService';
import {
  AttendanceDashboardPeriod,
  formatDashboardPeriodQuery,
  getLastMonthPeriod,
  parsePeriodFromSearchParams,
  periodToSearchParams
} from 'utils/attendanceDashboardPeriod';
import { formatEmployeeSubtitle } from 'utils/formatAttendanceEmployee';
import { toast } from 'react-toastify';
import '../../AttendanceEmployee/index.scss';

const PAGE_SIZE = 50;

export const ManagerAttendance = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState<AttendanceEmployeeSummary | null>(
    null
  );
  const [items, setItems] = useState<AttendanceEmployeeRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exportingScope, setExportingScope] = useState<'all' | 'selected' | null>(
    null
  );
  const [selectingAll, setSelectingAll] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<AttendanceEmployeeFilter>('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState<AttendanceDashboardPeriod>(() =>
    parsePeriodFromSearchParams(searchParams, getLastMonthPeriod())
  );
  const pageRef = useRef(1);
  const requestIdRef = useRef(0);

  const applyResponse = useCallback(
    (response: AttendanceEmployeeResponse, append: boolean) => {
      setSummary(response.summary);
      setTotalCount(response.totalCount);
      setItems((current) =>
        append ? [...current, ...response.items] : response.items
      );
      pageRef.current = response.page;
    },
    []
  );

  const fetchEmployees = useCallback(
    async (page: number, append: boolean) => {
      const requestId = ++requestIdRef.current;
      const { from, to } = formatDashboardPeriodQuery(period);

      const response = await agent.AttendanceTrackingService.getEmployeeSummary(
        {
          search,
          from,
          to,
          filter,
          page,
          pageSize: PAGE_SIZE
        }
      );

      if (requestId !== requestIdRef.current) return;

      applyResponse(response.data, append);
    },
    [applyResponse, filter, period, search]
  );

  const loadInitial = useCallback(async () => {
    setLoading(true);
    setSelectedIds(new Set());
    pageRef.current = 1;

    try {
      await fetchEmployees(1, false);
    } catch {
      setSummary(null);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [fetchEmployees]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore) return;
    if (items.length >= totalCount) return;

    setLoadingMore(true);

    try {
      await fetchEmployees(pageRef.current + 1, true);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchEmployees, items.length, loading, loadingMore, totalCount]);

  useEffect(() => {
    const timer = window.setTimeout(
      () => {
        loadInitial();
      },
      search.trim() ? 300 : 0
    );

    return () => window.clearTimeout(timer);
  }, [loadInitial, search]);

  useEffect(() => {
    document.querySelector('.attendance-tracking-content')?.scrollTo(0, 0);
  }, [location.pathname]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handleFilterChange = (nextFilter: AttendanceEmployeeFilter) => {
    setFilter(nextFilter);
  };

  const handlePeriodChange = (nextPeriod: AttendanceDashboardPeriod) => {
    setPeriod(nextPeriod);
    setSearchParams(periodToSearchParams(nextPeriod), { replace: true });
    setFilter('all');
    setSelectedIds(new Set());
  };

  const handleToggleRow = (employeeId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(employeeId)) {
        next.delete(employeeId);
      } else {
        next.add(employeeId);
      }
      return next;
    });
  };

  const handleToggleAll = async (checked: boolean) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }

    if (totalCount === 0) return;

    if (items.length >= totalCount) {
      setSelectedIds(new Set(items.map((row) => row.employeeId)));
      return;
    }

    setSelectingAll(true);
    const { from, to } = formatDashboardPeriodQuery(period);

    try {
      const response = await agent.AttendanceTrackingService.getEmployeeSummary({
        search,
        from,
        to,
        filter,
        page: 1,
        pageSize: totalCount
      });

      setSelectedIds(
        new Set(response.data.items.map((row) => row.employeeId))
      );
    } catch {
      toast.error('Unable to select all employees.');
    } finally {
      setSelectingAll(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleExport = async (scope: 'all' | 'selected') => {
    if (exportingScope) return;

    if (scope === 'selected' && selectedIds.size === 0) {
      toast.info('Select at least one employee to export.');
      return;
    }

    setExportingScope(scope);
    const { from, to } = formatDashboardPeriodQuery(period);

    try {
      await agent.AttendanceTrackingService.exportAttendanceExcel({
        from,
        to,
        search,
        filter,
        employeeIds: scope === 'selected' ? Array.from(selectedIds) : undefined
      });
      toast.success('Attendance export completed.');
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : 'Unable to export attendance records.';
      toast.error(message);
    } finally {
      setExportingScope(null);
    }
  };

  const hasMore = items.length < totalCount;

  return (
    <div className="attendance-employee-page">
      <AttendanceTrackingPageHeader
        title="Employee Attendance"
        description={formatEmployeeSubtitle(summary ?? undefined)}
        crumbs={[
          { label: 'Manager Portal' },
          { label: 'Employee Attendance' }
        ]}
        action={
          <AttendanceDashboardPeriodFilter
            period={period}
            onChange={handlePeriodChange}
          />
        }
      />

      <ManagerAttendancePanel
        summary={summary}
        items={items}
        totalCount={totalCount}
        loading={loading}
        loadingMore={loadingMore}
        exportingScope={exportingScope}
        selectingAll={selectingAll}
        hasMore={hasMore}
        period={period}
        onLoadMore={loadMore}
        onExport={handleExport}
        search={search}
        filter={filter}
        selectedIds={selectedIds}
        onSearchChange={handleSearchChange}
        onFilterChange={handleFilterChange}
        onToggleRow={handleToggleRow}
        onToggleAll={handleToggleAll}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
};
